import React, { useState } from 'react';
import { Clock, CheckCircle, AlertCircle, Edit3 } from 'lucide-react';

const DailyLogger = ({ routine, dailyLog, onLogUpdate, categories }) => {
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    actualTime: '',
    notes: '',
    completed: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const logData = {
      ...formData,
      loggedAt: new Date().toISOString()
    };
    
    onLogUpdate(editingEvent.id, logData);
    setEditingEvent(null);
    setFormData({
      actualTime: '',
      notes: '',
      completed: false
    });
  };

  const handleEdit = (event) => {
    const existingLog = dailyLog[event.id];
    setEditingEvent(event);
    setFormData({
      actualTime: existingLog?.actualTime || '',
      notes: existingLog?.notes || '',
      completed: existingLog?.completed || false
    });
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setFormData({
      actualTime: '',
      notes: '',
      completed: false
    });
  };

  const getTimeDifference = (idealTime, actualTime) => {
    if (!actualTime) return null;
    
    const ideal = new Date(`2000-01-01T${idealTime}:00`);
    const actual = new Date(`2000-01-01T${actualTime}:00`);
    const diffMs = actual - ideal;
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    
    if (diffMinutes === 0) return 'On time';
    if (diffMinutes > 0) return `+${diffMinutes}min late`;
    return `${Math.abs(diffMinutes)}min early`;
  };

  const getStatusColor = (event) => {
    const log = dailyLog[event.id];
    if (!log) return '#e9ecef';
    if (log.completed) return '#28a745';
    return '#ffc107';
  };

  const sortedRoutine = [...routine].sort((a, b) => a.idealTime.localeCompare(b.idealTime));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>Today's Log</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6c757d' }}>
          <Clock size={16} />
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {editingEvent && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3>Log Event: {editingEvent.title}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Actual Time</label>
                <input
                  type="time"
                  className="form-control time-input"
                  value={formData.actualTime}
                  onChange={(e) => setFormData({...formData, actualTime: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.completed}
                      onChange={(e) => setFormData({...formData, completed: e.target.checked})}
                    />
                    Completed
                  </label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                className="form-control notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="How did it go? Any observations? (e.g., 'Baby cried before nap', 'Ate 90ml', 'Poop 💩')"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn">
                Save Log
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        {sortedRoutine.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3>No routine events to log</h3>
            <p>Add some events to your routine first!</p>
          </div>
        ) : (
          sortedRoutine.map(event => {
            const category = categories[event.category];
            const log = dailyLog[event.id];
            const timeDiff = getTimeDifference(event.idealTime, log?.actualTime);
            const statusColor = getStatusColor(event);
            
            return (
              <div key={event.id} className="event-item" style={{ borderLeft: `4px solid ${statusColor}` }}>
                <div className="event-info">
                  <div className="event-title">
                    <span style={{ marginRight: '8px', fontSize: '20px' }}>
                      {category.icon}
                    </span>
                    {event.title}
                  </div>
                  <div className="event-time">
                    <Clock size={14} style={{ marginRight: '4px' }} />
                    <strong>Ideal:</strong> {event.idealTime}
                    {log?.actualTime && (
                      <>
                        {' • '}<strong>Actual:</strong> {log.actualTime}
                        {timeDiff && (
                          <span style={{ 
                            color: timeDiff === 'On time' ? '#28a745' : 
                                   timeDiff.includes('late') ? '#dc3545' : '#ffc107',
                            fontWeight: '600'
                          }}>
                            {' • '}{timeDiff}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {log?.notes && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#495057' }}>
                      <strong>Notes:</strong> {log.notes}
                    </div>
                  )}
                </div>
                <div className="event-actions">
                  {log?.completed ? (
                    <CheckCircle size={20} color="#28a745" />
                  ) : log ? (
                    <AlertCircle size={20} color="#ffc107" />
                  ) : null}
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEdit(event)}
                    style={{ padding: '8px 12px' }}
                  >
                    <Edit3 size={16} />
                    {log ? 'Edit' : 'Log'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DailyLogger; 